import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getUser } from '../../lib/storage';
import { UserRoleEnums } from '../../utility/userRoles';
import { Container } from './primitives';
import { BrandMark } from '../ui/BrandMark';
import { InitialsAvatar } from '../ui/InitialsAvatar';
import { Button } from '../ui/Button';
import { Surface } from '../ui/Surface';

interface NavigationItem {
  name: string;
  href: string;
  children?: NavigationItem[];
}

const USER_NAVIGATION_ITEMS: NavigationItem[] = [
  { name: 'Home', href: '/dashboard' },
  { name: 'Agent Marketplace', href: '/agent-types' },
  { name: 'My Agents', href: '/agents' },
  { name: 'ChatBots', href: '/chatbots' },
  { name: 'Workflows', href: '/workflows' },
  // Analytics is hidden until the page has real content (src/pages/Analytics.tsx is a placeholder).
  // { name: 'Analytics', href: '/analytics' },
  { name: 'Settings', href: '/settings' },
];

const SUPER_ADMIN_NAVIGATION_ITEMS: NavigationItem[] = [
  { name: 'All Clients', href: '/tenant-listing' },
  { name: 'Agent Type', href: '/super-agent-type' },
  {
    name: 'AI Pods',
    href: '/super-ai-pod',
    children: [
      { name: 'Manage PODS', href: '/super-ai-pod' },
      { name: 'Manage Plans', href: '/super-ai-pod-plans' },
      { name: 'Manage Features', href: '/super-features' },
    ],
  },
  { name: 'MCP Catalog', href: '/super-mcp-catalog' },
  { name: 'Categories', href: '/super-categories' },
  { name: 'Industries', href: '/super-industries' },
];

const ADMIN_NAVIGATION_ITEMS: NavigationItem[] = [...USER_NAVIGATION_ITEMS];

export const TopNavigation: React.FC = () => {
  const { logout } = useAuth();
  const location = useLocation();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const user = getUser();

  const role = user?.user.role;

  const allItems = useMemo(() => {
    if (role === UserRoleEnums.SUPER_ADMIN) return SUPER_ADMIN_NAVIGATION_ITEMS;
    if (role === UserRoleEnums.ADMIN) return ADMIN_NAVIGATION_ITEMS;
    return USER_NAVIGATION_ITEMS;
  }, [role]);

  // Page title and favicon are set from branding in one place
  // (BrandingProvider -> applyBranding), not here.

  const isActive = (href: string) => {
    if (href === '/dashboard') return location.pathname === href;
    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  };

  const isAgentConfigRoute = /^\/agents\/[^/]+\/config$/.test(location.pathname);
  const navSurfaceClass = isAgentConfigRoute
    ? 'bg-[linear-gradient(90deg,rgba(255,255,255,0.92)_0%,rgba(237,101,113,0.10)_100%)]'
    : 'bg-[rgb(var(--c-e7dff4,231_223_244)/0.40)]';

  return (
    <nav className="sticky top-0 z-50 px-4 py-4 sm:px-6 lg:px-8" aria-label="Primary">
      <Container size="wide" className="px-0 sm:px-0 lg:px-0">
        {/* data-theme-scope: a header colour from the theme replaces this
            background and keeps the text in it readable (theme/engine.ts). */}
        <div data-theme-scope="header" className={`flex items-center justify-between rounded-nav px-3 py-2.5 backdrop-blur-nav sm:px-4 md:px-8 md:py-3 ${navSurfaceClass}`}>
          <Link to="/dashboard" className="flex min-w-0 items-center">
            <BrandMark className="block h-12 w-auto sm:h-14 md:h-16" />
          </Link>

          <div className="hidden flex-1 items-center justify-center gap-2 xl:flex">
            {allItems.map((item) => {
              const active = isActive(item.href) || (item.children?.some((c) => isActive(c.href)) ?? false);
              if (!item.children) {
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    aria-current={active ? 'page' : undefined}
                    className={`rounded-xl px-4 py-2 text-sm font-medium leading-6 outline-none transition-colors focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-c-7f8ff2/80 focus-visible:ring-offset-2 focus-visible:ring-offset-c-ece6f5 ${
                      active
                        ? 'bg-gradient-to-r from-kv-agentic-purple to-kv-agentic-blue text-on-primary'
                        : 'text-kv-agentic-ink hover:bg-white/60'
                    }`}
                  >
                    {item.name}
                  </Link>
                );
              }

              const isOpen = openSubmenu === item.name;
              return (
                <div key={item.name} className="relative">
                  <Button tone="custom"
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={isOpen}
                    onClick={() => setOpenSubmenu((prev) => (prev === item.name ? null : item.name))}
                    className={`flex items-center gap-1 rounded-xl px-4 py-2 text-sm font-medium leading-6 outline-none transition-colors focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-c-7f8ff2/80 focus-visible:ring-offset-2 focus-visible:ring-offset-c-ece6f5 ${
                      active
                        ? 'bg-gradient-to-r from-kv-agentic-purple to-kv-agentic-blue text-on-primary'
                        : 'text-kv-agentic-ink hover:bg-white/60'
                    }`}
                  >
                    {item.name}
                    <svg className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </Button>

                  {isOpen && (
                    <Surface className="absolute left-0 mt-2 w-52 rounded-xl border border-c-e3e7f7 bg-white py-1 shadow-floating" role="menu">
                      {item.children.map((child) => (
                        <Link
                          key={child.name}
                          to={child.href}
                          onClick={() => setOpenSubmenu(null)}
                          className={`block px-4 py-2 text-sm ${isActive(child.href) ? 'font-medium text-kv-agentic-blue' : 'text-c-2f3353 hover:bg-c-f4f6fe'}`}
                        >
                          {child.name}
                        </Link>
                      ))}
                    </Surface>
                  )}
                </div>
              );
            })}
          </div>

          <div className="ml-auto flex min-w-0 items-center gap-2">
            <div className="relative">
              <Button tone="custom"
                type="button"
                aria-haspopup="menu"
                aria-expanded={isProfileOpen}
                onClick={() => setIsProfileOpen((prev) => !prev)}
                className="flex items-center gap-2 rounded-lg px-1 py-0.5 sm:gap-3"
              >
                <InitialsAvatar
                  name={user?.firstName || user?.name}
                  className="h-10 w-10 text-sm sm:h-11 sm:w-11 sm:text-base md:h-12 md:w-12"
                />
                <div className="hidden text-left lg:block">
                  <p className="text-sm font-semibold capitalize leading-5 text-kv-agentic-ink">
                    {user?.firstName || 'User'}
                  </p>
                  <p className="text-xs font-normal capitalize leading-4 text-kv-agentic-slate">
                    {String(user?.roles?.[0] || 'admin').toLowerCase()}
                  </p>
                </div>
              </Button>

              {isProfileOpen && (
                <Surface className="absolute right-0 mt-2 w-56 rounded-xl border border-c-e3e7f7 bg-white py-1 shadow-floating" role="menu">
                  <div className="border-b border-c-eef1fb px-4 py-2">
                    <p className="text-sm font-medium capitalize text-c-1f2447">{user?.firstName || 'User'}</p>
                    <p className="text-xs text-c-7f86aa">{user?.email || 'user@example.com'}</p>
                  </div>
                  <Link
                    to="/profile"
                    className="block px-4 py-2 text-sm text-c-2f3353 hover:bg-c-f4f6fe"
                    onClick={() => setIsProfileOpen(false)}
                  >
                    Profile Settings
                  </Link>
                  <Button tone="custom"
                    type="button"
                    onClick={() => {
                      logout();
                      setIsProfileOpen(false);
                    }}
                    className="block w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                  >
                    Sign out
                  </Button>
                </Surface>
              )}
            </div>

            <Button tone="custom"
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-c-cfd8f6 bg-white text-c-22284b xl:hidden"
              aria-label="Toggle navigation menu"
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-nav-menu"
              onClick={() => setIsMobileMenuOpen((prev) => !prev)}
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {isMobileMenuOpen ? (
                  <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" strokeLinejoin="round" />
                ) : (
                  <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" strokeLinejoin="round" />
                )}
              </svg>
            </Button>
          </div>
        </div>

        <div className="mt-3 min-w-0 xl:hidden">
          {isMobileMenuOpen && (
            <div
              id="mobile-nav-menu"
              className="space-y-2 rounded-xl border border-c-dfe5f8 bg-white/95 p-3 shadow-[0_12px_28px_rgb(var(--c-272e5c,39_46_92)/0.12)]"
              aria-label="Mobile navigation"
            >
              {allItems.map((item) => (
                <div key={item.name}>
                  <Link
                    to={item.href}
                    aria-current={isActive(item.href) ? 'page' : undefined}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`block rounded-lg px-3 py-2 text-sm font-medium outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-c-7f8ff2/80 ${
                      isActive(item.href)
                        ? 'bg-gradient-to-r from-kv-agentic-purple to-kv-agentic-blue text-on-primary'
                        : 'border border-c-dfe5f8 bg-white text-kv-agentic-ink'
                    }`}
                  >
                    {item.name}
                  </Link>
                  {item.children && (
                    <div className="mt-1 ml-3 space-y-1 border-l border-c-dfe5f8 pl-3">
                      {item.children.map((child) => (
                        <Link
                          key={child.name}
                          to={child.href}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={`block rounded-lg px-3 py-1.5 text-sm ${
                            isActive(child.href) ? 'font-medium text-kv-agentic-blue' : 'text-kv-agentic-ink/80'
                          }`}
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <Button tone="custom"
                type="button"
                onClick={() => {
                  logout();
                  setIsMobileMenuOpen(false);
                }}
                className="block w-full rounded-lg border border-rose-200 px-3 py-2 text-left text-sm font-medium text-rose-600"
              >
                Sign out
              </Button>
            </div>
          )}
        </div>
      </Container>
    </nav>
  );
};
